/**
 * Join Outing Page - Public page for joining an outing via invite link
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Calendar, MapPin, Trophy, Check, DollarSign, ArrowRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { useOutingApi } from '../hooks/useOutingApi';
import { formatDate, formatTime, formatCurrency, GAME_FORMATS } from '../utils/outingHelpers';

export default function JoinOutingPage({ user }) {
  const { outingId } = useParams();
  const navigate = useNavigate();
  const api = useOutingApi();
  
  const [outing, setOuting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [step, setStep] = useState('info'); // 'info', 'form', 'success'
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: ''
  });

  useEffect(() => {
    loadOuting();
  }, [outingId]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  const loadOuting = async () => {
    try {
      const data = await api.getOuting(outingId);
      if (data.success) {
        setOuting(data.outing);
      }
    } catch (err) {
      toast.error('Could not load outing details');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    
    setJoining(true);
    try {
      // Create a self-registration invitation
      const result = await api.sendInvitation(outingId, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        status: 'accepted', // Auto-accept since they're self-registering
        registered_via: 'invite_link'
      }, user?.user_id || 'guest');
      
      if (result.success) {
        setStep('success');
        toast.success('Successfully registered for the outing!');
      } else {
        toast.error(result.error || 'Failed to join');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to join outing');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="ps-theme-page min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!outing) {
    return (
      <div className="ps-theme-page min-h-screen p-4 flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Outing Not Found</h1>
        <p className="text-zinc-400 mb-4">This invite link may have expired or been removed.</p>
        <Button onClick={() => navigate('/')}>Go Home</Button>
      </div>
    );
  }

  const format = GAME_FORMATS[outing.game_settings?.front_9_format];

  if (step === 'success') {
    return (
      <div className="ps-theme-page min-h-screen p-4 flex flex-col items-center justify-center">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-6 animate-bounce">
          <Check className="w-10 h-10 text-green-400" />
        </div>
        <h1 className="text-2xl font-bold mb-2">You're In!</h1>
        <p className="text-zinc-400 text-center mb-6">
          You've successfully registered for<br />
          <span className="text-white font-semibold">{outing.name}</span>
        </p>
        <Card className="bg-[#121212] border-white/10 w-full max-w-sm">
          <CardContent className="p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Date</span>
              <span className="text-white">{formatDate(outing.event_date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Tee Time</span>
              <span className="text-white">{formatTime(outing.tee_time)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Course</span>
              <span className="text-white">{outing.course_name || 'TBD'}</span>
            </div>
          </CardContent>
        </Card>
        <p className="text-xs text-zinc-500 mt-6 text-center">
          The organizer will reach out with team assignments and payment details.
        </p>
        {user ? (
          <Button 
            onClick={() => navigate(`/outings/${outingId}`)}
            className="mt-4 bg-[#007AFF]"
          >
            View Outing Details
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button 
            onClick={() => navigate('/')}
            className="mt-4 bg-[#007AFF]"
          >
            Create Account
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="ps-theme-page min-h-screen" data-testid="join-outing-theme-page">
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-purple-900/50 to-[#0A0A0A] p-6 pb-8">
        <Badge className="bg-purple-500/20 text-purple-400 mb-4">You're Invited!</Badge>
        <h1 className="text-3xl font-black tracking-tight mb-2">{outing.name}</h1>
        {outing.description && (
          <p className="text-zinc-300 text-sm mb-4">{outing.description}</p>
        )}
        
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2 text-zinc-300">
            <Calendar className="w-4 h-4 text-purple-400" />
            {formatDate(outing.event_date)} at {formatTime(outing.tee_time)}
          </div>
          {outing.course_name && (
            <div className="flex items-center gap-2 text-zinc-300">
              <MapPin className="w-4 h-4 text-purple-400" />
              {outing.course_name}
            </div>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Event Details Card */}
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <h3 className="font-semibold text-white mb-3">Event Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-zinc-400">Format</p>
                <p className="text-white font-semibold flex items-center gap-1">
                  {format?.icon} {format?.name || outing.game_settings?.front_9_format}
                </p>
              </div>
              <div>
                <p className="text-zinc-400">Teams</p>
                <p className="text-white font-semibold">{outing.game_settings?.team_size || 4}-Person</p>
              </div>
              <div>
                <p className="text-zinc-400">Entry Fee</p>
                <p className="text-white font-semibold">{formatCurrency(outing.entry_fee)}</p>
              </div>
              {outing.game_settings?.skins?.enabled && (
                <div>
                  <p className="text-zinc-400">Skins Pot</p>
                  <p className="text-amber-400 font-semibold">${outing.game_settings.skins.total_pot}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Registration Form */}
        {step === 'info' && (
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-4 space-y-4">
              <h3 className="font-semibold text-white">Register to Play</h3>
              
              <div>
                <Label className="text-zinc-400 text-sm">Your Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-[#0D0D0D] border-white/20 text-white h-12 mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400 text-sm">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="bg-[#0D0D0D] border-white/20 text-white h-12 mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400 text-sm">Phone (optional)</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-[#0D0D0D] border-white/20 text-white h-12 mt-1"
                />
              </div>
              
              <Button
                onClick={handleJoin}
                disabled={joining || !formData.name || !formData.email}
                className="w-full h-12 bg-purple-600 hover:bg-purple-500 text-white font-semibold"
              >
                {joining ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Registering...
                  </div>
                ) : (
                  <>
                    <Users className="w-5 h-5 mr-2" />
                    Join This Outing
                  </>
                )}
              </Button>
              
              <p className="text-xs text-zinc-500 text-center">
                By registering, you agree to pay the entry fee to the organizer.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
